import { z } from 'zod'

/**
 * clipbook-api's API reference (CLI-1050): the OpenAPI 3.1 document its internal zone serves at
 * `GET /internal/openapi.json` (clipbook-api `src/docs/reference.ts`), which Headline renders behind the
 * workspace API page's gate. The same document as clipbook-api's `/openapi.json`, plus the quickstart
 * (`x-clipbook-guide`) and the MCP tools (`x-clipbook-mcp`).
 *
 * Parsed loosely on purpose: only what the page reads is checked (so a missing piece fails at the boundary
 * with its path named), and everything else passes through untouched for the "Download openapi.json" copy.
 */

/** A guide block: a paragraph (lines starting "- " are a list), an example, or a table. clipbook-api `render.ts`. */
export const apiGuideExampleSchema = z.object({
  label: z.string().optional(),
  lang: z.string(),
  code: z.string(),
})
export const apiGuideTableSchema = z.object({
  head: z.array(z.string()),
  rows: z.array(z.array(z.string())),
})
export const apiGuideBlockSchema = z.union([z.string(), apiGuideExampleSchema, apiGuideTableSchema])
export const apiGuideSchema = z.object({
  title: z.string(),
  lead: z.array(z.string()),
  sections: z.array(
    z.object({ id: z.string(), title: z.string(), content: z.array(apiGuideBlockSchema) }),
  ),
})
export type ApiGuide = z.infer<typeof apiGuideSchema>
export type ApiGuideBlock = z.infer<typeof apiGuideBlockSchema>
export type ApiGuideExample = z.infer<typeof apiGuideExampleSchema>
export type ApiGuideTable = z.infer<typeof apiGuideTableSchema>

/**
 * The MCP server and its tools. A tool's `inputSchema` is its arguments as JSON Schema, as the server's
 * tools/list serves them (clipbook-api #82); optional, so a document from before it still reads. Loose, so a
 * field the page does not read still reaches the openapi.json download.
 */
export const apiMcpToolSchema = z.looseObject({
  name: z.string(),
  description: z.string(),
  inputSchema: z.record(z.string(), z.unknown()).optional(),
})
export const apiMcpReferenceSchema = z.looseObject({
  url: z.string(),
  tools: z.array(apiMcpToolSchema),
})
export type ApiMcpTool = z.infer<typeof apiMcpToolSchema>
export type ApiMcpReference = z.infer<typeof apiMcpReferenceSchema>

export const clipbookApiOpenApiDocumentSchema = z.looseObject({
  openapi: z.string(),
  info: z.looseObject({
    title: z.string(),
    version: z.string(),
    description: z.string().optional(),
  }),
  servers: z.array(z.looseObject({ url: z.string() })).optional(),
  paths: z.record(z.string(), z.record(z.string(), z.unknown())),
  components: z.looseObject({ schemas: z.record(z.string(), z.unknown()).optional() }).optional(),
  'x-clipbook-guide': apiGuideSchema.optional(),
  'x-clipbook-mcp': apiMcpReferenceSchema.optional(),
})
export type ClipbookApiOpenApiDocument = z.infer<typeof clipbookApiOpenApiDocumentSchema>

/**
 * `GET /api/workspace-api/:subaccountId/reference`: the document, when Headline last read it, and whether
 * that read is older than it should be because the latest one failed (the page says so).
 */
export const apiReferenceSchema = z.object({
  fetchedAt: z.iso.datetime(),
  stale: z.boolean(),
  document: clipbookApiOpenApiDocumentSchema,
})
export type ApiReference = z.infer<typeof apiReferenceSchema>
